import type { MetaFunction } from "react-router"

import { BlogLayout, BlogProse } from "~/components/blog/BlogLayout"
import { BlogCta } from "~/components/blog/BlogCta"
import { WrittenBy } from "~/components/blog/WrittenBy"
import {
  A,
  H2,
  Lead,
  List,
  P,
  Quote,
  Strong,
  Turn,
} from "~/components/blog/prose"
import { getPost, postMeta } from "~/data/posts"

// The RetailNext integration announcement: a Pine59-side intro, a Q&A between
// RetailNext CEO Alexei Agratchev and Pine59 co-founder Thomas Walle, then a
// closing on what the combined view changes for retail teams.

const post = getPost("retailnext-partnership")!

export const meta: MetaFunction = ({ location }) =>
  postMeta(post, location.pathname)

export default function Post() {
  return (
    <BlogLayout post={post}>
      <BlogProse>
        <WrittenBy post={post} />

        <Lead>
          Retailers have never had more data about their stores. But much of
          that data still stops at the front door.
        </Lead>
        <P>
          At Pine59, we help retailers understand everything happening beyond
          the store. Who is in the market, where customers come from, where else
          they shop, how trade areas are changing, and how competitors are
          performing.
        </P>
        <P>
          RetailNext has spent years building the other side of that picture,
          helping retailers understand what happens once a customer walks
          through the door.
        </P>
        <P>Now we're bringing those two worlds together.</P>
        <P>
          The new integration between <A href="/">Pine59</A> and{" "}
          <A href="https://retailnext.net/">RetailNext</A> connects
          beyond-the-store market intelligence with in-store performance data,
          giving retailers a more complete view of the customer journey and,
          importantly, a better understanding of <em>why</em> individual stores
          perform the way they do.
        </P>
        <P>
          The relationship behind the partnership goes back years. RetailNext
          CEO Alexei Agratchev and Pine59 co-founder Thomas Walle first
          connected when the company was still known as Unacast. Alexei invited
          Thomas to RetailNext's Executive Forum to share his vision for
          location intelligence with a room full of retail leaders.
        </P>
        <P>
          What started as a conversation about where retail intelligence was
          heading has now become a product integration.
        </P>
        <P>
          Alexei and Thomas recently sat down to talk about how the market has
          changed, why connecting inside and outside intelligence matters, and
          what retailers can do when they finally see both sides of the store
          door.
        </P>

        <BlogCta />

        <H2>Five minutes with Alexei and Thomas</H2>

        <Turn speaker="Alexei">
          <P>
            You and I go back a long way. I remember what this pitch sounded
            like at Executive Forum, back when it was still just an idea and a
            few slides. Retail's changed a lot since then. What's changed most
            about the problem you're solving?
          </P>
        </Turn>
        <Turn speaker="Thomas">
          <P>
            Back then, location intelligence basically meant counting bodies.
            How many people visited and at what time. What's changed is that
            retailers stopped asking "how many" and started asking "who, and
            why." They want to know where a visitor came from, where else they
            shop, what their demographic profile looks like, and what
            competition is doing. The bar moved from measurement to explanation.
          </P>
          <P>
            And honestly, retailers got more sophisticated faster than most
            vendors did; they'd already squeezed everything they could out of
            their own four walls and hit a ceiling. What they were missing
            wasn't more data. It was context.
          </P>
        </Turn>

        <Turn speaker="Alexei">
          <P>
            We built RetailNext on the idea that the store tells the whole
            story. Millions of data points, all from inside four walls. But I
            keep hearing the same question from retailers: the product's right,
            the staffing's right, so why is this location still underperforming?
            Why do they need to see what's happening outside the store too?
          </P>
        </Turn>
        <Turn speaker="Thomas">
          <P>
            Because in-store data tells you what happened, not why. You can see
            traffic dipped on a Tuesday, conversion slipped in Q2, one location
            lags another that looks identical on paper. The store can't tell you
            why, because the answer usually isn't in the store. It's who's in
            the trade area, where they're going instead, which competitor just
            opened two miles away, whether the whole catchment softened or just
            your part of it.
          </P>
          <P>
            When a retailer tells me "the product's right, the staffing's right,
            and it's still underperforming," that's almost always a signal the
            explanation lives outside the store. You can't fix what you can't
            see, and half the picture is beyond the door.
          </P>
        </Turn>

        <Quote cite="Thomas Walle, Pine59 co-founder">
          You can't fix what you can't see, and half the picture is beyond
          the door.
        </Quote>

        <Turn speaker="Alexei">
          <P>
            Give me a real example. What decision does a retailer make
            differently with this combined view that they couldn't make before?
          </P>
        </Turn>
        <Turn speaker="Thomas">
          <P>
            Take an underperforming store. The classic question is: do we fix it
            or close it? Without outside data, that's a gut call, and usually an
            expensive one. With the combined view, you can tell the two apart.
            If foot traffic in the trade area is healthy but your capture rate
            is low, that's an execution problem - merchandising, staffing, hours
            - and it's fixable.
          </P>
          <P>
            If the whole catchment is drying up, people leaving the area, a
            competitor pulling them off, that's a location problem, and no
            amount of in-store tuning saves it. Same symptom, opposite decision.
            One keeps a store open and turns it around; the other stops you from
            pouring money into a location that was never going to work.
          </P>
          <P>
            Or, say a retailer's deciding where to open next. Their instinct is
            to build near their best-performing stores, clone the winner. But
            their in-store data only tells them those stores convert well, not
            why, or whether that magic repeats somewhere new. With the combined
            view, they can see what actually makes a location work - the visitor
            profile in the trade area, how people move through it, and which
            nearby brands pull the same customer.
          </P>
          <P>
            Then they can go find that pattern somewhere they don't have a store
            yet. Instead of guessing off last year's sales, they're matching
            real market DNA. That's the difference between expanding by hope and
            expanding by evidence, and it's the difference between a new store
            that ramps in months versus one that takes years, or never does.
          </P>
        </Turn>

        <Turn speaker="Alexei">
          <P>
            There's no shortage of companies that claim to do location
            intelligence. Most retailers I talk to are juggling two or three
            vendors already, and still missing the full picture. What's actually
            different here?
          </P>
        </Turn>
        <Turn speaker="Thomas">
          <P>
            Most retailers are stitching. One vendor for foot traffic, another
            for demographics, RetailNext for in-store, and a person whose real
            job becomes reconciling three datasets that don't agree. You lose
            time, you lose accuracy at every seam, and you still don't fully
            trust the answer. What's different is that this partnership between
            RetailNext and Pine59 is integrated.
          </P>
          <P>
            Outside and inside sit in the same view, calibrated to the same
            stores, so nobody's exporting CSVs and eyeballing whether the
            numbers line up. The integration removes the seam, and the seam was
            where most of the error and most of the wasted time lived.
          </P>
        </Turn>

        <Turn speaker="Alexei">
          <P>
            Let's say a retail exec only has budget for one of these: in-store
            or beyond-the-store data. Not both. From your perspective, what do
            they lose by picking just one?
          </P>
        </Turn>
        <Turn speaker="Thomas">
          <P>
            Either way, you're flying with one eye closed. In-store alone, you
            see execution in perfect detail and miss the market entirely -
            you'll optimize a store that's underperforming for reasons that
            might have nothing to do with the store. With beyond-the-store data
            alone, you understand the market but can't see whether you're
            actually converting it, so you can't tell a demand problem from a
            delivery problem. Neither half is wrong. They're just incomplete in
            opposite directions.
          </P>
          <P>
            The value was never in either dataset on its own. It's in the fact
            that one explains the other.
          </P>
        </Turn>

        <Quote cite="Thomas Walle, Pine59 co-founder">
          The value was never in either dataset on its own. It's in the fact
          that one explains the other.
        </Quote>

        <Turn speaker="Alexei">
          <P>
            Where does this go next? What does the retailer five years from now
            expect that they don't have today?
          </P>
        </Turn>
        <Turn speaker="Thomas">
          <P>
            Five years out, no retailer's going to accept the customer journey
            being split in two - outside data in one system, inside data in
            another, and a human trying to draw the line between them. They'll
            expect one continuous view, from the parking lot to the checkout
            counter, where what pulls someone toward a store and what happens
            once they're inside are read as a single story. That's the
            direction, and this integration is the first real piece of it.
          </P>
          <P>
            We're connecting the two halves that have always been managed
            separately, and once retailers see the whole journey in one place,
            they won't want to go back to guessing at the gap.
          </P>
        </Turn>

        <H2>From market potential to store performance</H2>
        <P>
          For us at Pine59, this partnership is about something bigger than
          combining two datasets.
        </P>
        <P>
          Retailers make decisions across a continuum. Where should we open? Who
          actually shops in this market? Which competitors are winning? Why is
          one store outperforming another? Are we facing a market problem or an
          execution problem? And where should we invest next?
        </P>
        <P>
          Historically, answering those questions meant moving between systems
          and trying to reconcile different versions of reality.
        </P>
        <P>
          Connecting Pine59's view of the world outside the store with
          RetailNext's view of what happens inside changes that.
        </P>
        <List
          items={[
            <>
              <Strong>For marketing teams,</Strong> it means understanding who
              visits your stores versus your competitors, and identifying the
              customer segments you're winning or missing.
            </>,
            <>
              <Strong>For real estate and expansion teams,</Strong> it means
              evaluating potential markets against the characteristics of the
              customers and locations already driving performance.
            </>,
            <>
              <Strong>For operations teams,</Strong> it means separating
              store-level execution problems from changes happening across the
              wider market.
            </>,
            <>
              <Strong>For retail leadership,</Strong> it means having one
              connected picture of market opportunity and store performance.
            </>,
          ]}
        />
        <P>
          The interesting part isn't simply having more data. It's being able to
          connect cause and effect.
        </P>
        <P>
          A store doesn't exist in isolation. Its performance is shaped by the
          people, competitors, neighborhoods and movement patterns around it,
          and then by what happens when those customers actually walk through
          the door.
        </P>
        <P>
          Pine59 and RetailNext can now connect those two halves. From outside
          the store to inside it. From market potential to actual performance.
          From seeing what happened to understanding why.
        </P>
        <P>That's the retail intelligence we've been working toward.</P>
      </BlogProse>
    </BlogLayout>
  )
}
